import React, { useState, useEffect, useMemo } from 'react';
import { executeHafQuery } from './services/hiveService';
import { SimulationData, HafSqlRow, PayoutEntry, GroupedPayout } from './types';

const formatNum = (val: number, decimals: number = 2) => {
  return val.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const formatUsd = (val: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(val);
};

const formatUsdSmall = (val: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(val);
};

type SortKey = keyof HafSqlRow;
type SortOrder = 'asc' | 'desc';

const App: React.FC = () => {
  const [data, setData] = useState<SimulationData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'desk' | 'forecast' | 'upcoming'>('desk');
  const [search, setSearch] = useState('');
  const [selectedAccount, setSelectedAccount] = useState<HafSqlRow | null>(null);
  const [selectedDay, setSelectedDay] = useState<GroupedPayout | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('remaining_mvests');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [visibleRows, setVisibleRows] = useState(10);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await executeHafQuery();
      if (!result.rows || result.rows.length === 0) {
        setError("LINK_OFFLINE: No active powerdowns detected.");
      }
      setData(result);
    } catch (err: any) {
      setError(err.message || "BRIDGE_FAULT: Internal communication failed.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const allPayoutEvents = useMemo(() => {
    if (!data) return [];
    const events: PayoutEntry[] = [];
    data.rows.forEach(row => {
      const remainingMvests = parseFloat(row.remaining_mvests as any);
      const rateMvests = parseFloat(row.rate_mvests as any);
      const installmentsDone = parseInt(row.installments_done as any);

      if (remainingMvests > 0) {
        const remainingSteps = 13 - installmentsDone;
        const baseDate = new Date(row.next_payout);
        const amountPerStep = rateMvests * data.hivePerMVest;

        for (let i = 0; i < Math.max(0, remainingSteps); i++) {
          const date = new Date(baseDate);
          date.setDate(baseDate.getDate() + (i * 7));
          events.push({
            account: row.account,
            date,
            amount: amountPerStep,
            currency: 'HIVE',
            type: 'POWER_DOWN',
            installmentIndex: installmentsDone + i + 1
          });
        }
      }
    });
    return events.sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [data]);

  const forecast = useMemo(() => {
    const grouped: { [key: string]: GroupedPayout } = {};
    allPayoutEvents.forEach(e => {
      const k = e.date.toISOString().split('T')[0];
      if (!grouped[k]) grouped[k] = { date: k, totalHive: 0, payouts: [] };
      grouped[k].totalHive += e.amount;
      grouped[k].payouts.push(e);
    });
    return Object.values(grouped).sort((a, b) => a.date.localeCompare(b.date));
  }, [allPayoutEvents]);

  const calendarDays = useMemo(() => {
    const days = [];
    const start = new Date();
    start.setHours(0,0,0,0);
    // Align to the start of the current week (Sunday) for a proper grid
    const dayOfWeek = start.getDay();
    start.setDate(start.getDate() - dayOfWeek);

    for (let i = 0; i < 98; i++) { // 14 weeks
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const k = d.toISOString().split('T')[0];
      const payoutData = forecast.find(f => f.date === k);
      days.push({
        date: d,
        key: k,
        payout: payoutData || null
      });
    }
    return days;
  }, [forecast]);

  const next50Events = useMemo(() => allPayoutEvents.slice(0, 50), [allPayoutEvents]);

  const sortedRows = useMemo(() => {
    if (!data) return [];
    const filtered = data.rows.filter(r => r.account.toLowerCase().includes(search.toLowerCase()));
    return [...filtered].sort((a, b) => {
      let valA = a[sortKey];
      let valB = b[sortKey];
      if (sortKey === 'next_payout') {
        valA = new Date(valA as string).getTime();
        valB = new Date(valB as string).getTime();
      }
      if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, search, sortKey, sortOrder]);

  const displayedRows = useMemo(() => sortedRows.slice(0, visibleRows), [sortedRows, visibleRows]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('desc');
    }
  };

  const peakDayValue = useMemo(() => {
    if (forecast.length === 0) return 1;
    return Math.max(...forecast.map(f => f.totalHive));
  }, [forecast]);

  const getDaysRemaining = (date: Date) => {
    const today = new Date();
    today.setHours(0,0,0,0);
    const payoutDay = new Date(date);
    payoutDay.setHours(0,0,0,0);
    const diffTime = payoutDay.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  return (
    <div className="flex h-screen w-screen overflow-hidden text-slate-400 bg-[#020617]">
      {/* Sidebar Navigation */}
      <aside className="w-16 lg:w-64 border-r border-slate-800 flex flex-col items-center lg:items-stretch z-40 relative bg-slate-950/20 backdrop-blur-xl">
        <div className="p-6 border-b border-slate-800 flex items-center gap-3 bg-slate-950/50">
          <div className="w-8 h-8 bg-red-600 rounded-sm flex items-center justify-center text-white shadow-[0_0_15px_rgba(220,38,38,0.4)]">
             <i className="fab fa-hive text-base"></i>
          </div>
          <span className="hidden lg:block text-white font-bold uppercase tracking-[0.1em] text-sm mono">PD_TRACKER</span>
        </div>

        <nav className="flex-1 px-3 py-10 space-y-2">
          <NavItem active={activeTab === 'desk'} onClick={() => setActiveTab('desk')} icon="desk" label="Data Matrix" />
          <NavItem active={activeTab === 'upcoming'} onClick={() => setActiveTab('upcoming')} icon="list" label="Next 50 Events" />
          <NavItem active={activeTab === 'forecast'} onClick={() => setActiveTab('forecast')} icon="chart" label="Calendar Flux" />
        </nav>

        <div className="hidden lg:block px-4 py-6 border-t border-slate-800 space-y-6">
           {/* Witness Note */}
           <div className="p-3 bg-red-600/5 border border-red-600/20 rounded-sm">
              <p className="text-[10px] mono leading-relaxed text-slate-500">
                This tool is provided to you by <span className="text-slate-200">louis88</span>. 
                If you find it helpful, vote for <span className="text-emerald-500">louis.witness</span> as a witness:
                <a href="https://vote.hive.uno/@louis.witness" target="_blank" rel="noopener noreferrer" className="block mt-1 text-red-500 hover:text-red-400 underline break-all">
                  vote.hive.uno/@louis.witness
                </a>
              </p>
           </div>
           
           <div className="space-y-4">
              <DiagnosticItem label="BRIDGE_LATENCY" value={data?.executionTime || '0ms'} />
              <DiagnosticItem label="HIVE_PRICE" value={data ? `$${data.hivePriceUsd.toFixed(3)}` : '$0.000'} />
              <DiagnosticItem label="WALLETS_INDEXED" value={data ? formatNum(data.rows.length, 0) : '0'} />
           </div>
        </div>

        <div className="p-4 border-t border-slate-800 bg-slate-950/50">
           <button 
             onClick={fetchData} 
             disabled={loading}
             className="w-full py-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-[10px] font-bold uppercase tracking-widest border border-emerald-500/30 transition-all rounded-sm"
           >
             {loading ? 'SYNCING...' : 'RESCAN GRID'}
           </button>
        </div>
      </aside>

      <main className="flex-1 h-full overflow-y-auto bg-[#020617]/40 relative custom-scrollbar">
        <div className="max-w-[1500px] mx-auto p-6 lg:p-10">
          
          <header className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-12 border-b border-slate-800 pb-8">
            <div className="flex items-center gap-6">
              <div className="h-12 w-1 bg-red-600 shadow-[0_0_15px_rgba(220,38,38,0.5)]"></div>
              <div>
                <h1 className="text-3xl font-black text-white tracking-tight uppercase mono leading-none mb-2">
                  {activeTab === 'desk' ? 'Tracker_Matrix' : activeTab === 'upcoming' ? 'Queue_Processing' : 'Calendar_Forecast'}
                </h1>
                <p className="text-[10px] font-mono text-slate-600 uppercase tracking-widest">
                  Live Market Feed: {data ? `$${data.hivePriceUsd.toFixed(3)}/HIVE` : 'DISCONNECTED'}
                </p>
              </div>
            </div>
            
            {error && (
               <div className="px-4 py-2 border border-red-500/50 bg-red-500/10 rounded-sm max-w-md">
                  <span className="text-[10px] font-black text-red-500 mono uppercase tracking-widest leading-tight">{error}</span>
               </div>
            )}
          </header>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            <MetricBox label="TOTAL_POWERDOWN" value={data ? formatNum(data.aggregates.totalRemainingHive, 0) : '0'} subValue={data ? formatUsd(data.aggregates.totalRemainingHive * data.hivePriceUsd) : '$0'} unit="HIVE" color="emerald" />
            <MetricBox label="WEEKLY_LIQUID_FLOW" value={data ? formatNum(data.aggregates.totalWeeklyHive, 0) : '0'} subValue={data ? formatUsd(data.aggregates.totalWeeklyHive * data.hivePriceUsd) : '$0'} unit="HIVE" color="blue" />
            <MetricBox label="CONVERSION_RATE" value={data ? data.hivePerMVest.toFixed(3) : '0'} subValue="HIVE / MVESTS" unit="H/MV" color="slate" />
            <MetricBox label="ACTIVE_CONTRACTS" value={data ? formatNum(data.rows.length, 0) : '0'} subValue="LIVE AUDITS" unit="WALLETS" color="emerald" />
          </div>

          {loading ? (
             <div className="h-64 flex flex-col items-center justify-center gap-4">
                <div className="w-10 h-10 border-2 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin"></div>
                <p className="text-[10px] font-black text-slate-700 uppercase mono animate-pulse">Establishing handshake with HAF cluster...</p>
             </div>
          ) : (
            <>
              {activeTab === 'desk' && (
                <div className="animate-in space-y-8">
                  <div className="flex-1 tech-border p-1 bg-slate-900/40 rounded-sm mb-4">
                    <input 
                      type="text" 
                      placeholder="SEARCH_INDEX..."
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      className="w-full bg-transparent p-3 pl-4 text-sm text-white mono outline-none placeholder-slate-800 uppercase"
                    />
                  </div>

                  <div className="border border-slate-800 bg-slate-950/50 shadow-2xl rounded-sm overflow-hidden">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-slate-900/50 text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 border-b border-slate-800">
                          <th className="px-6 py-4 cursor-pointer hover:bg-slate-800/50" onClick={() => toggleSort('account')}>IDENTITY</th>
                          <th className="px-6 py-4 text-right cursor-pointer hover:bg-slate-800/50" onClick={() => toggleSort('remaining_mvests')}>STAKE_REMAINING</th>
                          <th className="px-6 py-4 text-right cursor-pointer hover:bg-slate-800/50" onClick={() => toggleSort('rate_mvests')}>LIQUID_TICK</th>
                          <th className="px-6 py-4 text-center cursor-pointer hover:bg-slate-800/50" onClick={() => toggleSort('installments_done')}>PROGRESS</th>
                          <th className="px-6 py-4 text-right cursor-pointer hover:bg-slate-800/50" onClick={() => toggleSort('next_payout')}>SCHEDULED</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800/50 mono text-[13px]">
                        {displayedRows.map(row => {
                          const hiveRemaining = parseFloat(row.remaining_mvests as any) * (data?.hivePerMVest || 1);
                          const hiveTick = parseFloat(row.rate_mvests as any) * (data?.hivePerMVest || 1);
                          return (
                            <tr key={row.account} onClick={() => setSelectedAccount(row)} className="hover:bg-emerald-500/[0.03] transition-all group cursor-pointer">
                              <td className="px-6 py-4 flex items-center gap-3">
                                <img src={`https://images.hive.blog/u/${row.account}/avatar`} className="w-6 h-6 border border-slate-700 bg-slate-800" alt="" />
                                <span className="text-white font-bold group-hover:text-emerald-400">@{row.account}</span>
                              </td>
                              <td className="px-6 py-4 text-right">
                                <div className="text-slate-400">{formatNum(hiveRemaining, 0)}</div>
                                <div className="text-[10px] text-slate-700">{formatUsd(hiveRemaining * (data?.hivePriceUsd || 0))}</div>
                              </td>
                              <td className="px-6 py-4 text-right">
                                <div className="text-emerald-400 font-bold">+{formatNum(hiveTick, 2)}</div>
                                <div className="text-[10px] text-emerald-900">{formatUsdSmall(hiveTick * (data?.hivePriceUsd || 0))}</div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-2">
                                  <div className="flex-1 h-1 bg-slate-800"><div className="h-full bg-emerald-500" style={{ width: `${(parseInt(row.installments_done as any)/13)*100}%` }}></div></div>
                                  <span className="text-[9px] w-8 text-center">{row.installments_done}/13</span>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-right text-slate-600">{new Date(row.next_payout).toLocaleDateString()}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    
                    {sortedRows.length > visibleRows && (
                       <div className="p-4 bg-slate-900/30 border-t border-slate-800 text-center">
                          <button 
                            onClick={() => setVisibleRows(prev => prev + 50)}
                            className="text-[10px] font-black text-emerald-500 uppercase tracking-[0.2em] hover:text-white transition-colors py-2 px-8 bg-emerald-500/5 rounded-sm border border-emerald-500/10"
                          >
                            LOAD_MORE_RECORDS ({sortedRows.length - visibleRows} REMAINING)
                          </button>
                       </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'upcoming' && (
                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-in">
                    {next50Events.map((event, i) => {
                       const days = getDaysRemaining(event.date);
                       return (
                          <div key={i} className="tech-border bg-slate-900/40 p-4 flex flex-col gap-4 hover:border-emerald-500/50 transition-colors">
                             <div className="flex justify-between items-start">
                                <div className="flex items-center gap-2">
                                   <img src={`https://images.hive.blog/u/${event.account}/avatar`} className="w-8 h-8 border border-slate-700" alt="" />
                                   <span className="text-white font-bold">@{event.account}</span>
                                </div>
                                <span className={`text-[10px] font-black px-2 py-0.5 rounded-sm mono ${days <= 1 ? 'bg-red-500/20 text-red-500' : 'bg-slate-800 text-slate-500'}`}>T-{days}D</span>
                             </div>
                             <div className="flex items-end justify-between mt-auto">
                                <div className="flex flex-col">
                                   <span className="text-xs mono text-slate-600">{event.date.toDateString()}</span>
                                   <span className="text-[10px] mono text-emerald-900 font-bold uppercase tracking-tighter">EST: {formatUsdSmall(event.amount * (data?.hivePriceUsd || 0))}</span>
                                </div>
                                <span className="text-xl font-black text-emerald-400 mono">+{formatNum(event.amount, 1)} HIVE</span>
                             </div>
                          </div>
                       )
                    })}
                 </div>
              )}

              {activeTab === 'forecast' && (
                 <div className="animate-in space-y-12">
                   <div className="grid grid-cols-7 gap-px bg-slate-800 border border-slate-800 rounded-sm overflow-hidden shadow-2xl">
                     {['SUN','MON','TUE','WED','THU','FRI','SAT'].map(day => (
                       <div key={day} className="bg-slate-950 p-3 text-center border-b border-slate-800">
                         <span className="text-[10px] font-black text-slate-600 tracking-widest">{day}</span>
                       </div>
                     ))}
                     
                     {calendarDays.map((day) => {
                       const intensity = day.payout ? (day.payout.totalHive / peakDayValue) * 100 : 0;
                       const isToday = new Date().toISOString().split('T')[0] === day.key;
                       
                       return (
                         <div key={day.key} className={`bg-slate-950 min-h-[140px] p-3 relative group border border-slate-800/30 flex flex-col ${isToday ? 'ring-1 ring-inset ring-red-500/50' : ''}`}>
                           <div className="flex justify-between items-start mb-2">
                             <span className={`text-[11px] font-black mono ${isToday ? 'text-white' : 'text-slate-700'}`}>
                               {day.date.getDate()}
                             </span>
                             {day.payout && (
                                <button 
                                  onClick={() => setSelectedDay(day.payout)}
                                  className="text-[9px] font-bold text-emerald-500 px-2 py-1 bg-emerald-500/5 hover:bg-emerald-500/20 rounded-sm border border-emerald-500/20 transition-all mono uppercase"
                                >
                                  {day.payout.payouts.length} PD
                                </button>
                             )}
                           </div>

                           {day.payout && (
                             <div className="flex-1 flex flex-col justify-end">
                               <div className="text-[14px] font-black text-emerald-400 mono leading-tight mb-0.5">
                                 +{formatNum(day.payout.totalHive, 0)}
                               </div>
                               <div className="text-[10px] font-bold text-slate-700 mono leading-none mb-3">
                                 {formatUsd(day.payout.totalHive * (data?.hivePriceUsd || 0))}
                               </div>
                               
                               {/* Volume Progress Bar */}
                               <div className="w-full h-1 bg-slate-900 rounded-full overflow-hidden">
                                  <div className="h-full bg-emerald-500 transition-all shadow-[0_0_10px_rgba(16,185,129,0.3)]" style={{ width: `${Math.max(5, intensity)}%` }}></div>
                               </div>
                             </div>
                           )}
                           
                           {isToday && (
                             <div className="absolute top-0 right-0 p-1">
                               <div className="w-1.5 h-1.5 bg-red-600 rounded-full animate-pulse shadow-[0_0_5px_#dc2626]"></div>
                             </div>
                           )}
                         </div>
                       );
                     })}
                   </div>

                   <div className="p-8 tech-border bg-slate-950/30 border-slate-800">
                      <div className="flex flex-col md:flex-row items-center gap-8 text-xs mono text-slate-500">
                         <div className="flex items-center gap-6">
                           <div className="flex items-center gap-2"><div className="w-2 h-2 bg-emerald-500/20 rounded-xs"></div> LOW_VOL</div>
                           <div className="flex items-center gap-2"><div className="w-2 h-2 bg-emerald-500/60 rounded-xs"></div> MID_VOL</div>
                           <div className="flex items-center gap-2"><div className="w-2 h-2 bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)] rounded-xs"></div> PEAK_VOL</div>
                         </div>
                         <div className="md:ml-auto italic opacity-50">90_DAY_LIQUIDITY_PROJECTION_ENGINE_ACTIVE</div>
                      </div>
                   </div>
                 </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Daily Detail Modal */}
      {selectedDay && data && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-md animate-in">
          <div className="w-full max-w-2xl bg-slate-900 border border-slate-700 shadow-2xl rounded-sm overflow-hidden flex flex-col max-h-[85vh]">
            <div className="p-6 border-b border-slate-800 flex justify-between items-center bg-slate-950/50">
              <div className="flex items-baseline gap-4">
                <h2 className="text-2xl font-black text-white mono uppercase italic tracking-tighter">
                  {new Date(selectedDay.date).toDateString()}
                </h2>
                <span className="text-[10px] text-emerald-500 font-bold mono">LIQUID_FLOW_TOTAL</span>
              </div>
              <button onClick={() => setSelectedDay(null)} className="p-2 text-slate-500 hover:text-white transition-colors mono text-lg font-bold">✕</button>
            </div>
            
            <div className="grid grid-cols-2 gap-px bg-slate-800 border-b border-slate-800">
               <div className="p-6 bg-slate-900">
                 <p className="text-[10px] text-slate-600 font-black uppercase mono mb-1">TOTAL_HIVE_RELEASE</p>
                 <p className="text-3xl font-black text-emerald-400 mono">+{formatNum(selectedDay.totalHive, 2)}</p>
               </div>
               <div className="p-6 bg-slate-900">
                 <p className="text-[10px] text-slate-600 font-black uppercase mono mb-1">ESTIMATED_USD_VALUE</p>
                 <p className="text-3xl font-black text-white mono">{formatUsd(selectedDay.totalHive * data.hivePriceUsd)}</p>
               </div>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar bg-slate-950/20">
              <table className="w-full text-left border-collapse">
                <thead className="sticky top-0 bg-slate-900/95 backdrop-blur-sm z-10">
                  <tr className="text-[9px] font-black text-slate-500 uppercase tracking-widest border-b border-slate-800">
                    <th className="px-6 py-4">IDENTITY</th>
                    <th className="px-6 py-4 text-right">QUANTUM</th>
                    <th className="px-6 py-4 text-right">VALUATION</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50 mono text-xs">
                  {selectedDay.payouts.sort((a, b) => b.amount - a.amount).map((p, i) => (
                    <tr key={i} className="hover:bg-emerald-500/[0.02]">
                      <td className="px-6 py-4 flex items-center gap-3 text-white font-bold">
                        <img src={`https://images.hive.blog/u/${p.account}/avatar`} className="w-5 h-5 border border-slate-700" alt="" />
                        @{p.account}
                      </td>
                      <td className="px-6 py-4 text-right text-emerald-500 font-bold">+{formatNum(p.amount, 2)}</td>
                      <td className="px-6 py-4 text-right text-slate-500">{formatUsdSmall(p.amount * data.hivePriceUsd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="p-4 border-t border-slate-800 bg-slate-950/50 text-[9px] text-slate-700 mono italic text-center uppercase tracking-widest">
               End of report for {selectedDay.date} index
            </div>
          </div>
        </div>
      )}

      {/* Account Detail Modal */}
      {selectedAccount && data && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in">
           <div className="w-full max-w-xl bg-slate-900 border border-slate-700 p-8 rounded-sm relative shadow-2xl">
              <button onClick={() => setSelectedAccount(null)} className="absolute top-4 right-4 text-slate-500 hover:text-white mono">CLOSE_X</button>
              <div className="flex items-center gap-6 mb-8">
                 <img src={`https://images.hive.blog/u/${selectedAccount.account}/avatar`} className="w-16 h-16 border border-slate-700 shadow-xl" alt="" />
                 <div>
                    <h2 className="text-3xl font-black text-white mono uppercase italic tracking-tighter">@{selectedAccount.account}</h2>
                    <span className="text-[10px] font-black text-slate-600 uppercase mono">Powerdown Audit Report</span>
                 </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-8">
                 <div className="p-4 bg-slate-950 border border-slate-800">
                    <p className="text-[9px] text-slate-600 uppercase mono mb-1">Total_Remaining_USD</p>
                    <p className="text-xl font-bold text-white mono">{formatUsd(parseFloat(selectedAccount.remaining_mvests as any) * data.hivePerMVest * data.hivePriceUsd)}</p>
                    <p className="text-[10px] text-slate-800 font-mono">{formatNum(parseFloat(selectedAccount.remaining_mvests as any) * data.hivePerMVest, 0)} HIVE</p>
                 </div>
                 <div className="p-4 bg-slate-950 border border-slate-800">
                    <p className="text-[9px] text-emerald-600 uppercase mono mb-1">Weekly_Liquid_Flux</p>
                    <p className="text-xl font-bold text-emerald-400 mono">+{formatNum(parseFloat(selectedAccount.rate_mvests as any) * data.hivePerMVest, 2)}</p>
                    <p className="text-[10px] text-emerald-900 font-mono">EST: {formatUsdSmall(parseFloat(selectedAccount.rate_mvests as any) * data.hivePerMVest * data.hivePriceUsd)}</p>
                 </div>
              </div>
              <div className="max-h-48 overflow-y-auto custom-scrollbar border border-slate-800 p-2 bg-slate-950/30">
                 {Array.from({ length: 13 - parseInt(selectedAccount.installments_done as any) }).map((_, i) => (
                    <div key={i} className="flex justify-between py-2 border-b border-slate-800 last:border-0 mono text-[10px]">
                       <span className="text-slate-600">{new Date(new Date(selectedAccount.next_payout).getTime() + (i * 7 * 86400000)).toDateString()}</span>
                       <div className="flex items-center gap-4">
                          <span className="text-slate-800">{formatUsdSmall(parseFloat(selectedAccount.rate_mvests as any) * data.hivePerMVest * data.hivePriceUsd)}</span>
                          <span className="text-emerald-500 font-bold">+{formatNum(parseFloat(selectedAccount.rate_mvests as any) * data.hivePerMVest, 2)} HIVE</span>
                       </div>
                    </div>
                 ))}
              </div>
           </div>
        </div>
      )}
    </div>
  );
};

const NavItem = ({ active, onClick, icon, label }: any) => {
  const icons: any = {
    desk: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path></svg>,
    chart: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>,
    list: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"></path></svg>
  };
  return (
    <button onClick={onClick} className={`w-full flex items-center gap-4 px-4 py-3 border transition-all ${active ? 'bg-emerald-500 border-emerald-400 text-slate-950 shadow-[0_0_15px_rgba(16,185,129,0.3)]' : 'border-transparent text-slate-600 hover:text-slate-300'}`}>
      {icons[icon]}
      <span className="hidden lg:block text-[10px] font-black uppercase tracking-widest mono">{label}</span>
    </button>
  );
};

const MetricBox = ({ label, value, subValue, unit, color }: any) => {
  const colors: any = { emerald: 'text-emerald-400', blue: 'text-blue-400', slate: 'text-white' };
  return (
    <div className="bg-slate-950/50 border border-slate-800 p-5 relative overflow-hidden group rounded-sm min-h-[100px] flex flex-col justify-center backdrop-blur-sm">
      <div className="absolute top-0 left-0 w-1 h-full bg-slate-800 group-hover:bg-emerald-500 transition-colors z-10"></div>
      <div className="relative z-10">
        <p className="text-[9px] font-black text-slate-700 uppercase tracking-widest mb-3 mono">{label}</p>
        <div className="flex items-baseline gap-2">
          <span className={`text-2xl font-black mono tracking-tighter ${colors[color]}`}>{value}</span>
          {unit && <span className="text-[9px] font-bold text-slate-800 uppercase mono">{unit}</span>}
        </div>
        {subValue && <div className="text-[10px] font-bold text-slate-700 mono mt-1">{subValue}</div>}
      </div>
    </div>
  );
};

const DiagnosticItem = ({ label, value }: any) => (
  <div>
    <p className="text-[9px] font-black text-slate-800 uppercase tracking-widest mono mb-1">{label}</p>
    <span className="text-xs font-bold text-slate-500 mono">{value}</span>
  </div>
);

export default App;